import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  addAccommodationAttributeData,
  addBuilding,
  addBuildingRoom,
  addBuildingRoomArea,
  addBuildingRoomBed,
  fetchAccommodationAttributeDetails,
  fetchAccommodationAttributeOptions,
  fetchAllAccommodationAttributes,
  fetchAllAccommodationBuildings,
  fetchAllBeds,
  fetchAllBuildingRoomAreas,
  fetchAllBuildingRooms,
  fetchAllBuildngs,
  fetchAllFloors,
  fetchAllRoomAreas,
  fetchAllRooms,
  fetchAttributeTypes,
  removeBuilding,
  removeBuildingRoom,
  removeBuildingRoomArea,
  removeBuildingRoomBed,
  updateAccommodationAttribute,
  updateAttributeIndexes,
  updateBuilding,
  updateBuildingRoom,
  updateBuildingRoomArea,
  updateBuildingRoomBed,
} from '../../common/api/accommodationAttributesService';
import { RootState } from '../../app/store';
import { LOADING_STATUS } from '../../common/constants';
import {
  AddBuilding,
  AddBuildingRoom,
  AddBuildingRoomArea,
  RemoveBuilding,
  RemoveBuildingRoom,
  RemoveBuildingRoomArea,
} from './accommodationsSlice';

export interface AccommodationAttributeDetailsTranslations {
  keyId: string;
  locale: string;
  text: string;
}

export interface AttributeRoomTranslations {
  keyId: string;
  locale: string;
  text: string;
}

export interface RoomDataItemTranslations {
  keyId: string;
  locale: string;
  text: string;
}

export interface TranslationItem {
  keyId: string;
  locale: string;
  text: string;
}

export interface AttributeRoomAreaTranslations {
  keyId: string;
  locale: string;
  text: string;
}
export interface AccommodationAttributeOptionItem {
  id: string;
  nameFi: string;
  nameEn: string;
  nameRu: string;
  index: number;
}
export interface AccommodationAttributeListItem {
  id: string;
  shortName: string;
  name: string;
  groupName: string;
  index: number;
  attributeType: {
    id: string;
    typeName: string;
    nameFi: string;
    nameEn: string;
    nameRu: string;
  };
  optionItems: AccommodationAttributeOptionItem[];
}

export interface AccommodationDetailsAttributeOptionItem {
  id: string;
  nameFi: string;
  nameEn: string;
  nameRu: string;
}

export interface UpdateAttributeIndexItem {
  id: string;
  index: number;
}

export interface UpdateAttributeIndexCommand {
  indexes: UpdateAttributeIndexItem[];
}
export interface AccommodationAttributeDetailsData {
  id: string;
  isDeleted: boolean;
  isVisibleInWebShop: boolean;
  isVisibleInAccommodation: boolean;
  isSearchCriteria: boolean;
  hasNoValue: boolean;
  showIcon: boolean;
  yesIcon: string;
  noIcon: string;
  groupNumber: number;
  attributeType: {
    id: string;
    isDeleted: boolean;
    typeName: string;
    nameFi: string;
    nameEn: string;
    nameRu: string;
  };
  optionItems: AccommodationAttributeOptionItem[];
  translations: AccommodationAttributeDetailsTranslations[];
}

export interface AttributeRoomData {
  id: string;
  isDeleted: boolean;
  roomId: string;
  roomAreaId: string;
  translations: AttributeRoomTranslations[];
}

export interface AttributeRoomAreaData {
  id: string;
  isDeleted: boolean;
  roomId: string;
  roomAreaId: string;
  translations: AttributeRoomAreaTranslations[];
}

export interface RoomBedData {
  id: string;
  isDeleted: boolean;
  amount: number;
  width: number;
  length: number;
  bedId: string;
  translations: TranslationItem[];
}

export interface BuildingRoomData {
  id: string;
  isDeleted: boolean;
  roomId: string;
  floorId: string;
  translations: RoomDataItemTranslations[];
  beds: RoomBedData[];
}

export interface AllBeds {
  id: string;
  isDeleted: boolean;
  beds: RoomBedData[];
}

export interface UpdateBuildingRoomBedItem {
  RoomBedId: string;
  BedId: string;
  Amount: number;
  Width: number;
  Length: number;
}

export interface AddBuildingRoomBedItem {
  BuildingRoomId: string;
}

export interface RemoveBuildingRoomBedItem {
  RoomBedId: string;
}

export interface UpdateBuildingRoomItem {
  FloorId: string;
  Id: string;
}

export interface UpdateBuildingRoomAreaItem {
  Id: string;
}
export interface BuildingRoomBedData {
  id: string;
  isDeleted: boolean;
  roomId: string;
  floorId: string;
  translations: AttributeRoomTranslations[];
}

export interface BuildingRoomAreaData {
  id: string;
  isDeleted: boolean;
  roomAreaId: string;
  translations: AttributeRoomTranslations[];
}

export interface BuildingTranslations {
  keyId: string;
  locale: string;
  text: string;
}
export interface BuildingData {
  id: string;
  rooms: BuildingRoomData[];
  roomAreas: BuildingRoomAreaData[];
  translations: BuildingTranslations[];
  availabilityStartDate: string;
  availabilityEndDate: string;
}

export interface BuildingUpdateData {
  buildingId: string;
  availabilityStartDate: string;
  availabilityEndDate: string;
}

export interface FloorTranslations {
  keyId: string;
  locale: string;
  text: string;
}
export interface FloorData {
  id: string;
  translations: FloorTranslations[];
}

export interface BuildingListData {
  id: string;
  translations: BuildingTranslations[];
}

export interface AccommodationAttributeEmptyData {
  add: string;
}

export interface AccommodationAttributeQueryFilters {
  pageNumber: number;
  pageSize: number;
}

export interface RoomsAndAreasFilter {
  pageNumber: number;
  pageSize: number;
}
export interface BuildingRoomsQueryFilters {
  id: string;
  pageNumber: number;
  pageSize: number;
}

export interface AccommodationBuildingsQueryFilters {
  id: string;
  pageNumber: number;
  pageSize: number;
}

export interface AttributeTypeItem {
  id: string;
  typeName: string;
  nameFi: string;
  nameEn: string;
  nameRu: string;
}
export interface AccommodationAttributesState {
  accommodationAttributeItems: AccommodationAttributeListItem[];
  attributeTypes: AttributeTypeItem[];
  accommodationAttributeOptionItems: AccommodationAttributeOptionItem[];
  attributeRoomItems: AttributeRoomData[];
  attributeRoomAreaItems: AttributeRoomAreaData[];
  buildings: BuildingData[];
  allBuildings: BuildingListData[];
  allBuildingsLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  allBeds: BuildingRoomBedData[];
  allBedsLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  allFloors: FloorData[];
  allFloorsLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  buildingsLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  roomAreasStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  roomAreaItems: AttributeRoomAreaData[];
  roomsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  roomItems: AttributeRoomData[];
  accommodationAttributesStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  accommodationAttributeOptionsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  accommodationAttributeDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  attributeTypesStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  attributeRoomsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  attributeRoomAreasStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;

  accommodationAttributeDetailsData: AccommodationAttributeDetailsData;
  accommodationAttributeFilter: AccommodationAttributeQueryFilters;
}

const initialState: AccommodationAttributesState = {
  accommodationAttributeItems: [],
  accommodationAttributeOptionItems: [],
  attributeRoomItems: [],
  attributeRoomAreaItems: [],
  buildings: [],
  buildingsLoadingStatus: LOADING_STATUS.IDLE,
  accommodationAttributesStatus: LOADING_STATUS.IDLE,
  accommodationAttributeDetailsStatus: LOADING_STATUS.IDLE,
  accommodationAttributeOptionsStatus: LOADING_STATUS.IDLE,
  attributeTypesStatus: LOADING_STATUS.IDLE,
  attributeRoomsStatus: LOADING_STATUS.IDLE,
  attributeRoomAreasStatus: LOADING_STATUS.IDLE,
  roomAreasStatus: LOADING_STATUS.IDLE,
  roomItems: [],
  roomAreaItems: [],
  roomsStatus: LOADING_STATUS.IDLE,
  allBeds: [],
  allBedsLoadingStatus: LOADING_STATUS.IDLE,
  accommodationAttributeDetailsData: {
    id: '',
    isDeleted: false,
    isSearchCriteria: false,
    isVisibleInAccommodation: false,
    isVisibleInWebShop: false,
    hasNoValue: false,
    noIcon: '',
    yesIcon: '',
    showIcon: false,
    groupNumber: 0,
    attributeType: {
      id: '',
      isDeleted: false,
      typeName: '',
      nameFi: '',
      nameEn: '',
      nameRu: '',
    },
    optionItems: [],
    translations: [],
  },
  attributeTypes: [],
  accommodationAttributeFilter: {
    pageNumber: 1,
    pageSize: 200,
  },
  allBuildings: [],
  allBuildingsLoadingStatus: LOADING_STATUS.IDLE,
  allFloorsLoadingStatus: LOADING_STATUS.IDLE,
  allFloors: [],
};

export const updateAccommodationAttributeAsync = createAsyncThunk(
  'accommodations/updateAccommodationAttribute',
  async (
    accommodationAttributeData: AccommodationAttributeDetailsData,
    { rejectWithValue },
  ) => {
    try {
      const response = await updateAccommodationAttribute(
        accommodationAttributeData,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateBuildingRoomBedAsync = createAsyncThunk(
  'accommodations/updateBuildingRoomBed',
  async (roomBedItem: UpdateBuildingRoomBedItem, { rejectWithValue }) => {
    try {
      const response = await updateBuildingRoomBed(roomBedItem);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);
export const updateBuildingAsync = createAsyncThunk(
  'accommodations/updateBuilding',
  async (roomBedItem: BuildingUpdateData, { rejectWithValue }) => {
    try {
      const response = await updateBuilding(roomBedItem);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const addBuildingRoomBedAsync = createAsyncThunk(
  'accommodations/addBuildingRoomBed',
  async (roomBedItem: AddBuildingRoomBedItem, { rejectWithValue }) => {
    try {
      const response = await addBuildingRoomBed(roomBedItem);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const removeBuildingRoomBedAsync = createAsyncThunk(
  'accommodations/removeBuildingRoomBed',
  async (roomBedItem: RemoveBuildingRoomBedItem, { rejectWithValue }) => {
    try {
      const response = await removeBuildingRoomBed(roomBedItem);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);
export const updateBuildingRoomAsync = createAsyncThunk(
  'accommodations/updateBuildingRoom',
  async (roomBedItem: UpdateBuildingRoomItem, { rejectWithValue }) => {
    try {
      const response = await updateBuildingRoom(roomBedItem);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateBuildingRoomAreaAsync = createAsyncThunk(
  'accommodations/updateBuildingRoomArea',
  async (roomBedItem: UpdateBuildingRoomAreaItem, { rejectWithValue }) => {
    try {
      const response = await updateBuildingRoomArea(roomBedItem);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateAttributeIndexesAsync = createAsyncThunk(
  'accommodations/updateAttributeIndexesAsync',
  async (
    accommodationAttributeData: UpdateAttributeIndexCommand,
    { rejectWithValue },
  ) => {
    try {
      const response = await updateAttributeIndexes(accommodationAttributeData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const addBuildingRoomAreaAsync = createAsyncThunk(
  'accommodations/addBuildingRoomArea',
  async (roomArea: AddBuildingRoomArea, { rejectWithValue }) => {
    try {
      const response = await addBuildingRoomArea(roomArea);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const addBuildingAsync = createAsyncThunk(
  'accommodations/addBuilding',
  async (building: AddBuilding, { rejectWithValue }) => {
    if (!building.buildingId === undefined || building.buildingId === null) {
      return rejectWithValue('Building not selected');
    }
    try {
      const response = await addBuilding(building);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const addBuildingRoomAsync = createAsyncThunk(
  'accommodations/addBuildingRoom',
  async (room: AddBuildingRoom, { rejectWithValue }) => {
    try {
      const response = await addBuildingRoom(room);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const removeBuildingRoomAsync = createAsyncThunk(
  'accommodations/removeBuildingRoomAsync',
  async (room: RemoveBuildingRoom, { rejectWithValue }) => {
    try {
      const response = await removeBuildingRoom(room);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);
export const removeBuildingAsync = createAsyncThunk(
  'accommodations/removeBuildingAsync',
  async (building: RemoveBuilding, { rejectWithValue }) => {
    try {
      const response = await removeBuilding(building);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const removeBuildingRoomAreaAsync = createAsyncThunk(
  'accommodations/removeBuildingRoomAreaAsync',
  async (room: RemoveBuildingRoom, { rejectWithValue }) => {
    try {
      const response = await removeBuildingRoomArea(room);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const removeBuildingRoomAeraAsync = createAsyncThunk(
  'accommodations/removeBuildingRoomAeraAsync',
  async (room: RemoveBuildingRoomArea, { rejectWithValue }) => {
    try {
      const response = await removeBuildingRoomArea(room);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);
export const addAccommodationAttributeDataAsync = createAsyncThunk(
  'accommodations/addAccommodationAttributeData',
  async (
    accommodationAttributeData: AccommodationAttributeEmptyData,
    { rejectWithValue },
  ) => {
    try {
      const response = await addAccommodationAttributeData(
        accommodationAttributeData,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAllAccommodationAttributesAsync = createAsyncThunk(
  'accommodations/getAllAccommodationAttributes',
  async (
    filterCriteria: AccommodationAttributeQueryFilters,
    { rejectWithValue },
  ) => {
    try {
      // Do some cheking;
      const response = await fetchAllAccommodationAttributes(filterCriteria);
      return response.result;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAllBuildingRoomsAsync = createAsyncThunk(
  'accommodations/getBuildingRooms',
  async (filterCriteria: BuildingRoomsQueryFilters, { rejectWithValue }) => {
    try {
      // Do some cheking;
      const response = await fetchAllBuildingRooms(filterCriteria);
      return response.result.rooms;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAllAccommodationBuildingsAsync = createAsyncThunk(
  'accommodations/getAccommodationBuildings',
  async (
    filterCriteria: AccommodationBuildingsQueryFilters,
    { rejectWithValue },
  ) => {
    try {
      // Do some cheking;
      const response = await fetchAllAccommodationBuildings(filterCriteria);
      return response.result.buildings;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAllRoomsAsync = createAsyncThunk(
  'accommodations/getAllRooms',
  async (filterCriteria: RoomsAndAreasFilter, { rejectWithValue }) => {
    try {
      // Do some cheking;
      const response = await fetchAllRooms(filterCriteria);
      return response.result.rooms;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAllBuildingsAsync = createAsyncThunk(
  'accommodations/getAllBuildings',
  async (filterCriteria: RoomsAndAreasFilter, { rejectWithValue }) => {
    try {
      // Do some cheking;
      const response = await fetchAllBuildngs(filterCriteria);
      return response.result.buildings;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAllFloorsAsync = createAsyncThunk(
  'accommodations/getAllFloors',
  async (filterCriteria: RoomsAndAreasFilter, { rejectWithValue }) => {
    try {
      // Do some cheking;
      const response = await fetchAllFloors(filterCriteria);
      return response.result.floors;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAllRoomAreasAsync = createAsyncThunk(
  'accommodations/getAllRoomAreas',
  async (filterCriteria: RoomsAndAreasFilter, { rejectWithValue }) => {
    try {
      // Do some cheking;
      const response = await fetchAllRoomAreas(filterCriteria);
      return response.result.roomAreas;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAllBedsAsync = createAsyncThunk(
  'accommodations/getallbeds',
  async (filterCriteria: RoomsAndAreasFilter, { rejectWithValue }) => {
    try {
      // Do some cheking;
      const response = await fetchAllBeds(filterCriteria);
      return response.result.beds;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAllBuildingRoomAreasAsync = createAsyncThunk(
  'accommodations/getAllBuildingRoomAreas',
  async (filterCriteria: BuildingRoomsQueryFilters, { rejectWithValue }) => {
    try {
      // Do some cheking;
      const response = await fetchAllBuildingRoomAreas(filterCriteria);
      return response.result.roomAreas;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAccommodationAttributeDetailsAsync = createAsyncThunk(
  'accommodations/getAccommodationAttributeDetails',
  async (accommodationAttributeId: string, { rejectWithValue }) => {
    try {
      const response = await fetchAccommodationAttributeDetails(
        accommodationAttributeId,
      );
      return response.result;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAccommodationAttributeOptionsAsync = createAsyncThunk(
  'accommodations/getAccommodationAttributeOptions',
  async (accommodationAttributeId: string, { rejectWithValue }) => {
    try {
      const response = await fetchAccommodationAttributeOptions(
        accommodationAttributeId,
      );
      return response.data;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAttributesTypesAsync = createAsyncThunk(
  'accommodations/getAttributeTypes',
  async (
    filterCriteria: AccommodationAttributeQueryFilters,
    { rejectWithValue },
  ) => {
    try {
      // Do some cheking;
      const response = await fetchAttributeTypes(filterCriteria);
      return response.result.attributeTypes;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

const accommodationAttributesSlice = createSlice({
  name: 'accommodationattributes',
  initialState,
  reducers: {
    clearAccommodationAttributeDetails: state => {
      state.accommodationAttributeDetailsData =
        initialState.accommodationAttributeDetailsData;
      state.accommodationAttributeOptionItems =
        initialState.accommodationAttributeOptionItems;
      state.accommodationAttributeDetailsStatus = LOADING_STATUS.IDLE;
      state.accommodationAttributeOptionsStatus = LOADING_STATUS.IDLE;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getAllAccommodationAttributesAsync.pending, state => {
        state.accommodationAttributesStatus = LOADING_STATUS.LOADING;
      })
      .addCase(
        getAllAccommodationAttributesAsync.fulfilled,
        (state, action) => {
          state.accommodationAttributesStatus = LOADING_STATUS.IDLE;
          state.accommodationAttributeItems = action.payload;
        },
      )
      .addCase(getAllAccommodationAttributesAsync.rejected, state => {
        state.accommodationAttributesStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAccommodationAttributeDetailsAsync.pending, state => {
        state.accommodationAttributeDetailsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(
        getAccommodationAttributeDetailsAsync.fulfilled,
        (state, action) => {
          state.accommodationAttributeDetailsStatus = LOADING_STATUS.IDLE;
          state.accommodationAttributeDetailsData = action.payload;
        },
      )
      .addCase(getAccommodationAttributeDetailsAsync.rejected, state => {
        state.accommodationAttributeDetailsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(
        getAccommodationAttributeOptionsAsync.fulfilled,
        (state, action) => {
          state.accommodationAttributeOptionsStatus = LOADING_STATUS.IDLE;
          state.accommodationAttributeOptionItems = action.payload;
        },
      )
      .addCase(getAccommodationAttributeOptionsAsync.rejected, state => {
        state.accommodationAttributeOptionsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAttributesTypesAsync.fulfilled, (state, action) => {
        state.attributeTypesStatus = LOADING_STATUS.IDLE;
        state.attributeTypes = action.payload;
      })
      .addCase(getAllBuildingRoomsAsync.fulfilled, (state, action) => {
        state.attributeRoomsStatus = LOADING_STATUS.IDLE;
        state.attributeRoomItems = action.payload;
      })
      .addCase(getAllBuildingRoomsAsync.rejected, state => {
        state.attributeRoomsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAllBuildingRoomAreasAsync.rejected, state => {
        state.attributeRoomAreasStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAllBuildingRoomAreasAsync.fulfilled, (state, action) => {
        state.attributeRoomAreasStatus = LOADING_STATUS.IDLE;
        state.attributeRoomAreaItems = action.payload;
      })
      .addCase(getAllRoomAreasAsync.pending, state => {
        state.roomAreasStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAllRoomAreasAsync.rejected, state => {
        state.roomAreasStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAllRoomAreasAsync.fulfilled, (state, action) => {
        state.roomAreasStatus = LOADING_STATUS.IDLE;
        state.roomAreaItems = action.payload;
      })
      .addCase(getAllRoomsAsync.pending, state => {
        state.roomsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAllRoomsAsync.rejected, state => {
        state.roomsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAllRoomsAsync.fulfilled, (state, action) => {
        state.roomsStatus = LOADING_STATUS.IDLE;
        state.roomItems = action.payload;
      })
      .addCase(getAllAccommodationBuildingsAsync.pending, state => {
        state.buildingsLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAllAccommodationBuildingsAsync.rejected, state => {
        state.buildingsLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAllAccommodationBuildingsAsync.fulfilled, (state, action) => {
        state.buildingsLoadingStatus = LOADING_STATUS.IDLE;
        state.buildings = action.payload;
      })
      .addCase(getAllBuildingsAsync.pending, state => {
        state.allBuildingsLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAllBuildingsAsync.rejected, state => {
        state.allBuildingsLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAllBuildingsAsync.fulfilled, (state, action) => {
        state.allBuildingsLoadingStatus = LOADING_STATUS.IDLE;
        state.allBuildings = action.payload;
      })
      .addCase(getAllFloorsAsync.pending, state => {
        state.allFloorsLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAllFloorsAsync.rejected, state => {
        state.allFloorsLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAllFloorsAsync.fulfilled, (state, action) => {
        state.allFloorsLoadingStatus = LOADING_STATUS.IDLE;
        state.allFloors = action.payload;
      })
      .addCase(getAllBedsAsync.pending, state => {
        state.allBedsLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAllBedsAsync.rejected, state => {
        state.allBedsLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAllBedsAsync.fulfilled, (state, action) => {
        state.allBedsLoadingStatus = LOADING_STATUS.IDLE;
        state.allBeds = action.payload;
      });
  },
});

export const { clearAccommodationAttributeDetails } =
  accommodationAttributesSlice.actions;

export const selectAccommodationAttributes = (state: RootState) =>
  state.accommodationattributes.accommodationAttributeItems;
export const selectAttributeRooms = (state: RootState) =>
  state.accommodationattributes.attributeRoomItems;
export const selectAttributeRoomAreas = (state: RootState) =>
  state.accommodationattributes.attributeRoomAreaItems;
export const selectAccommodationAttributesLoadingStatus = (state: RootState) =>
  state.accommodationattributes.accommodationAttributesStatus;
export const selectAccommodationAttributeDetails = (state: RootState) =>
  state.accommodationattributes.accommodationAttributeDetailsData;
export const selectAttributeTypes = (state: RootState) =>
  state.accommodationattributes.attributeTypes;
export const selectAttributeTypesLoadingStatus = (state: RootState) =>
  state.accommodationattributes.attributeTypesStatus;
export const selectAccommodationAttributeDetailsLoadingStatus = (
  state: RootState,
) => state.accommodationattributes.accommodationAttributeDetailsStatus;
export const selectAccommodationAttributeOptions = (state: RootState) =>
  state.accommodationattributes.accommodationAttributeOptionItems;
export const selectAccommodationAttributeOptionsLoadingStatus = (
  state: RootState,
) => state.accommodationattributes.accommodationAttributeOptionsStatus;
export const selectAttributeRoomsLoadingStatus = (state: RootState) =>
  state.accommodationattributes.attributeRoomsStatus;
export const selectAttributeRoomAreasLoadingStatus = (state: RootState) =>
  state.accommodationattributes.attributeRoomAreasStatus;

export const selectRoomAreasLoadingStatus = (state: RootState) =>
  state.accommodationattributes.roomAreasStatus;

export const selectRoomsLoadingStatus = (state: RootState) =>
  state.accommodationattributes.roomsStatus;

export const selectRooms = (state: RootState) =>
  state.accommodationattributes.roomItems;

export const selectRoomAreas = (state: RootState) =>
  state.accommodationattributes.roomAreaItems;

export const selectAccommodationBuildings = (state: RootState) =>
  state.accommodationattributes.buildings;

export const selectAccommodationBuildingsLoadingStatus = (state: RootState) =>
  state.accommodationattributes.buildingsLoadingStatus;

export const selectAllBuildingsLoadingStatus = (state: RootState) =>
  state.accommodationattributes.allBuildingsLoadingStatus;
export const selectAllBuildings = (state: RootState) =>
  state.accommodationattributes.allBuildings;

export const selectAllFloorsLoadingStatus = (state: RootState) =>
  state.accommodationattributes.allFloorsLoadingStatus;
export const selectAllFloors = (state: RootState) =>
  state.accommodationattributes.allFloors;

export const selectAllBedsLoadingStatus = (state: RootState) =>
  state.accommodationattributes.allBedsLoadingStatus;
export const selectAllBeds = (state: RootState) =>
  state.accommodationattributes.allBeds;

export default accommodationAttributesSlice.reducer;
